import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { Container, CustomButton } from '@mindly/ui-components';
import PageHeader from '../../components/PageHeader/PageHeader';
import { FormattedMessage, useIntl } from 'react-intl';
import { SubscriptionsPlansFooter, SubscriptionsPlansWrapper, SubscriptionsTable } from './SubscriptionsPlans.style';
import { informationCircle } from 'ionicons/icons';
import { useDispatch, useSelector } from 'react-redux';
import { subscriptionsInfoSelector } from '../../redux/subscriptionsInfo/selector';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { setIsActive } from '../../redux/tabBar/reducer';
import { fetchSubscriptionsInfo } from '../../redux/subscriptionsInfo/thunk';
import { userSelector } from '../../redux/user/selector';
import { RouteComponentProps } from 'react-router';
import useLangs from '../../context/LangsContext';
import { ISpecialist, SupportedCurrencies } from '../../redux/specialists/types';
import { PossibleNumberOfSessionsPerWeekType } from '../../redux/subscriptionsInfo/types';
import SubscriptionRow from './SubscriptionRow/SubscriptionRow';
import { ImpactStyle } from '@capacitor/haptics';
import SubscriptionsPlansSkeleton from './SubscriptionsPlansSkeleton';
import { getPlanName } from '../../utils/getPlanName';
import SubscriptionAdvantages from '../../components/SubscriptionAdvantages';
import { WHAT_PLAN_THERAPY } from '../../intercom-articles';
import useToast from '../../context/ToastContext/ToastContext';
import { loadUserThunk } from '../../redux/user/thunk';
import { useAnalytics, useAuthFlow } from '../../shared';
import { fetchSpecialistByID } from '../../redux/loadedSpecialists/thunk';
import { specialistSelectorPositionById } from '../../redux/specialists/selector';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

interface SubscriptionsPlansProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const SubscriptionsPlans: FC<SubscriptionsPlansProps> = ({ match, history }) => {
  const { id } = match.params;

  const { displayArticle, logEvent } = useIntercomPlugin();
  const dispatch = useDispatch();
  const { locale } = useLangs();
  const intl = useIntl();
  const { showToast } = useToast();
  const { authUser } = useAuthFlow();
  const { trackPredefinedEvent } = useAnalytics();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PossibleNumberOfSessionsPerWeekType | null>(null);
  const [activePlan, setActivePlan] = useState(3);
  const [isEventTracked, setIsEventTracked] = useState(false);
  const [isPageView, setIsPageView] = useState(false);

  const currentUser = useSelector(userSelector);
  const [specialist, setSpecialist] = useState<ISpecialist | null>(null);
  const { data: subscriptionsInfo } = useSelector(subscriptionsInfoSelector());
  const specialistPosition = useSelector(specialistSelectorPositionById(specialist?.id ?? ''));

  useIonViewWillEnter(async () => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    try {
      setIsLoading(true);
      dispatch(setIsActive(false));

      const localSpecialist = await dispatch(fetchSpecialistByID(id)).unwrap();
      if (!localSpecialist) throw new Error('no_such_therapist');
      setSpecialist(localSpecialist);

      const result = await dispatch(
        fetchSubscriptionsInfo({
          userID: currentUser?.id,
          therapistID: id,
        })
      ).unwrap();
      setSelectedPlan(result.possible_number_of_sessions_per_week[0]);

      if (authUser?.uid) {
        await dispatch(loadUserThunk(authUser.uid));
      }
    } catch (error) {
      console.error(error);
      showToast({ error });
      history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
    } finally {
      setIsLoading(false);
    }
  }, [id, specialist, subscriptionsInfo, locale, authUser?.uid]);

  useIonViewWillEnter(() => {
    logEvent('Enter subscription selection page');
  }, []);

  useIonViewDidEnter(() => {
    setIsEventTracked(false);
    setIsPageView(true);
  });

  useIonViewWillLeave(() => {
    setIsPageView(false);
  });

  useIonViewDidLeave(() => {
    setSelectedPlan(null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isPageView) return;
    if (isEventTracked) return;
    if (!specialist) return;
    if (!currentUser) return;

    setIsEventTracked(true);
    trackPredefinedEvent('SUBSCRIPTION_PAGE_VIEWED', {
      specialist_id: id,
      specialist_name: `${specialist?.first_name} ${specialist?.last_name}`,
      specialist_order_position: specialistPosition,
    });
  }, [isEventTracked, isPageView, specialist, trackPredefinedEvent, currentUser, specialistPosition]);

  const handleNextStep = useCallback(() => {
    history.push(
      `${PrivateRoutes.SUBSCRIPTION_PAYMENT.replace(':id', specialist?.id ?? '')
        .replace(':monthCount', `${activePlan}`)
        .replace(':selectedPlan', selectedPlan ? selectedPlan.value.toString() : '')}`
    );
  }, [specialist?.id, activePlan, selectedPlan]);

  if (isLoading) {
    return (
      <IonPage>
        <PageHeader backChevron={true} intercomId={WHAT_PLAN_THERAPY} />
        <IonContent>
          <Container>
            <SubscriptionsPlansSkeleton />
          </Container>
        </IonContent>
        <SubscriptionsPlansFooter>
          <IonSkeletonText
            animated
            style={{
              display: 'inline-block',
              margin: 0,
              padding: 0,
              width: '100%',
              height: '47px',
              borderRadius: '6px',
            }}
          />
        </SubscriptionsPlansFooter>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <PageHeader backChevron={true} intercomId={WHAT_PLAN_THERAPY} />
      <IonContent>
        <Container>
          <SubscriptionsPlansWrapper>
            <span className={'plans-step'}>
              <FormattedMessage
                id={'bocking.step'}
                defaultMessage={'Крок {from} із {to}'}
                values={{
                  from: 1,
                  to: 2,
                }}
              />
            </span>
            <h3 className={'plans-title'}>
              <FormattedMessage id={'subscription.plans.title'} defaultMessage={'Оберіть підписку на терапію'} />
            </h3>
            <SubscriptionAdvantages style={{ marginBottom: '20px' }} showWeekMessage={false} />
            <IonSegment
              mode={'ios'}
              className={'plans-segment'}
              value={`${activePlan}`}
              onIonChange={async (e) => {
                isomorficHapticsImpact({ style: ImpactStyle.Light });
                setActivePlan(e.detail?.value ? +e.detail.value : 0);
              }}
            >
              {subscriptionsInfo?.possible_intervals_of_months?.length &&
                selectedPlan &&
                specialist?.session_price &&
                subscriptionsInfo?.discounts &&
                subscriptionsInfo.possible_intervals_of_months.map((el, index) => (
                  <IonSegmentButton key={index} className={'plans-segment-button'} value={`${el}`}>
                    <IonLabel className={'plans-label'}>{getPlanName(el, intl)}</IonLabel>
                  </IonSegmentButton>
                ))}
            </IonSegment>
            <SubscriptionsTable>
              {subscriptionsInfo?.possible_intervals_of_months?.length &&
                selectedPlan &&
                specialist?.session_price &&
                subscriptionsInfo?.discounts &&
                subscriptionsInfo.possible_intervals_of_months.map((el, index) => (
                  <SubscriptionRow
                    key={index}
                    isActive={activePlan === el}
                    monthInterval={el}
                    sessionPrice={specialist.session_price}
                    sessionsPerWeek={+selectedPlan.value}
                    discountObject={subscriptionsInfo.discounts}
                    currency={specialist?.session_price_type ?? SupportedCurrencies.UAH}
                    onClickHandler={setActivePlan}
                  />
                ))}
            </SubscriptionsTable>
            <button
              className={'plans-footer-help'}
              onClick={async () => {
                await displayArticle(WHAT_PLAN_THERAPY);
              }}
            >
              <IonIcon icon={informationCircle} />{' '}
              <FormattedMessage id={'subscription.plans.what'} defaultMessage={'Що таке підписка на терапію?'} />
            </button>
          </SubscriptionsPlansWrapper>
        </Container>
      </IonContent>
      <SubscriptionsPlansFooter>
        <CustomButton buttonType={'primary'} onClick={handleNextStep}>
          <FormattedMessage id={'general.button.chouse'} defaultMessage={'Вибрати'} />
        </CustomButton>
      </SubscriptionsPlansFooter>
    </IonPage>
  );
};

export default SubscriptionsPlans;
